/*!
 * JustSmoke.me - conversational product search (JS AI Search)
 *
 * An add-on to the EXISTING search box. Plain keyword searches are left alone (no code runs, no
 * request is made). Requests such as "I've got 10 pounds", "cheapest King Palm" or "a bong and a
 * grinder for 25 pounds" are answered by a rules-based engine in this file, using the compact
 * catalogue index, and every product is checked live against /products/<handle>.js before it is
 * shown (in stock, price). Only when the rules cannot understand a request is the optional AI
 * interpreter asked (a server-side proxy with a hard monthly spending cap); the AI only turns the
 * sentence into search words and price limits, it never supplies products, prices or stock.
 * Any failure falls back silently to the normal search.
 *
 * The rules engine mirrors the Python reference in prototype/jsassistant. Shared test vectors keep
 * the two in step (tests/js/run_vectors.html).
 *
 * Switches: window.JSAI.enabled = false, or add ?jsai=off to a page URL (remembered for the tab).
 */
(function (root) {
  'use strict';
  var X = root.JSAIX = root.JSAIX || {};

  var POUND = '£';
  var MAX_QUERY_CHARS = 300;

  // ------------------------------------------------------------------ text helpers (nlp.py)
  var CONTROL_RE = /[\x00-\x1f\x7f]+/g;
  var WS_RE = /\s+/g;
  var TOKEN_RE = /[a-z0-9]+(?:\.[0-9]+)?/g;

  function cleanText(v) {
    if (v === null || v === undefined) return '';
    return String(v).replace(CONTROL_RE, ' ').replace(WS_RE, ' ').trim();
  }

  function asciiFold(value) {
    value = value.replace(/’/g, '').replace(/‘/g, '').replace(/'/g, '');
    var out = '';
    for (var i = 0; i < value.length; i++) {
      var ch = value.charAt(i);
      if (ch.charCodeAt(0) < 128) { out += ch; continue; }
      var base = ch.normalize ? ch.normalize('NFKD').replace(/[^\x00-\x7f]/g, '') : '';
      out += base ? base : ' ';
    }
    return out;
  }

  function tokenize(value) {
    return asciiFold(cleanText(value)).toLowerCase().match(TOKEN_RE) || [];
  }

  function stem(token) {
    if (token.length <= 3 || /[0-9]/.test(token)) return token;
    if (/ies$/.test(token) && token.length > 4) token = token.slice(0, -3) + 'i';
    else if (/(sses|shes|ches|xes)$/.test(token)) token = token.slice(0, -2);
    else if (/(ss|us|is)$/.test(token)) { /* keep */ }
    else if (/s$/.test(token)) token = token.slice(0, -1);
    if (token.length > 4 && /ie$/.test(token)) token = token.slice(0, -2) + 'i';
    else if (token.length > 3 && /y$/.test(token) && 'aeiou'.indexOf(token.charAt(token.length - 2)) < 0) token = token.slice(0, -1);
    return token;
  }

  function stemTokens(value) { return tokenize(value).map(stem); }

  function osaDistance(a, b) {
    if (a === b) return 0;
    var la = a.length, lb = b.length;
    if (!la) return lb;
    if (!lb) return la;
    var prev2 = null, prev = [], cur, i, j;
    for (j = 0; j <= lb; j++) prev.push(j);
    for (i = 1; i <= la; i++) {
      cur = [i];
      for (j = 1; j <= lb; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur.push(v);
      }
      prev2 = prev; prev = cur;
    }
    return prev[lb];
  }

  // ------------------------------------------------------------------ budget parsing (nlp.parse_budget)
  function num(name) { return '(?<' + name + '>\\d{1,5}(?:\\.\\d{1,2})?)(?!\\.?[0-9a-z])'; }
  var MONEY_WORDS = '(?:pounds?|quid|gbp|nickers?|squid)\\b';
  var NUMBER_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    eleven: 11, twelve: 12, fifteen: 15, twenty: 20, thirty: 30, forty: 40, fifty: 50 };
  var BETWEEN_RE = new RegExp('\\bbetween\\s*' + POUND + '?\\s*' + num('a') + '\\s*(?:and|to|-)\\s*' + POUND + '?\\s*' + num('b') + '(?:\\s*' + MONEY_WORDS + ')?', 'g');
  var MIN_RE = new RegExp('\\b(?:over|above|more than|at least|minimum|min)\\s*(?:' + POUND + '\\s*' + num('a') + '|' + num('b') + '\\s*' + MONEY_WORDS + ')', 'g');
  var MAX_RE = new RegExp('\\b(?:under|below|less than|lower than|up to|upto|max|maximum|within|at most|no more than|budget of|budget)\\s*(?:' + POUND + '\\s*)?' + num('a') + '(?:\\s*' + MONEY_WORDS + ')?', 'g');
  var MONEY_RE = new RegExp(POUND + '\\s*' + num('a') + '|' + num('b') + '\\s*' + MONEY_WORDS + '|\\bgbp\\s*' + num('c'), 'g');
  var WORD_MONEY_RE = new RegExp('\\b(?<w>' + Object.keys(NUMBER_WORDS).join('|') + ')\\s*' + MONEY_WORDS, 'g');
  var SLANG_RE = /\b(?<w>tenner|fiver)\b/g;
  var PENCE_RE = /\b(?<a>\d{1,3})\s*p\b/g;

  function toPence(s) { return Math.round(parseFloat(s) * 100); }
  function firstGroup(g, names) {
    for (var i = 0; i < names.length; i++) if (g[names[i]] !== undefined) return toPence(g[names[i]]);
    return null;
  }

  /** Returns {min, max} in pence (null when absent) and the text with the budget words removed. */
  function parseBudget(text) {
    var t = ' ' + String(text).toLowerCase().replace(/,/g, ' ') + ' ';
    var st = { min: null, max: null };
    t = t.replace(BETWEEN_RE, function () {
      var g = arguments[arguments.length - 1];
      var a = toPence(g.a), b = toPence(g.b);
      if (a <= b) { st.min = a; st.max = b; } else { st.min = b; st.max = a; }
      return ' ';
    });
    t = t.replace(MIN_RE, function () { var g = arguments[arguments.length - 1]; if (st.min === null) st.min = firstGroup(g, ['a', 'b']); return ' '; });
    t = t.replace(MAX_RE, function () { var g = arguments[arguments.length - 1]; if (st.max === null) st.max = firstGroup(g, ['a']); return ' '; });
    t = t.replace(MONEY_RE, function () { var g = arguments[arguments.length - 1]; if (st.max === null) st.max = firstGroup(g, ['a', 'b', 'c']); return ' '; });
    t = t.replace(WORD_MONEY_RE, function () { var g = arguments[arguments.length - 1]; if (st.max === null) st.max = NUMBER_WORDS[g.w] * 100; return ' '; });
    t = t.replace(SLANG_RE, function () { var g = arguments[arguments.length - 1]; if (st.max === null) st.max = g.w === 'tenner' ? 1000 : 500; return ' '; });
    if (st.max === null) {
      t = t.replace(PENCE_RE, function () { var g = arguments[arguments.length - 1]; st.max = parseInt(g.a, 10); return ' '; });
    }
    return { min: st.min, max: st.max, present: st.min !== null || st.max !== null, rest: t.trim() };
  }

  // ------------------------------------------------------------------ intent (intent.py)
  var CUES = ['i have', 'ive got', 'i got', 'i need', 'i want', 'im looking', 'looking for', 'show me', 'do you have',
    'do you sell', 'do you stock', 'have you got', 'what can', 'what is', 'whats', 'what are', 'which', 'recommend',
    'suggest', 'something', 'anything', 'similar', 'like this', 'same as', 'alternative', 'instead of', 'how much',
    'gift for', 'for under', 'cheapest', 'cheaper', 'cheap', 'best', 'budget', 'can i get', 'can you', 'could you', 'help me'];
  var CUE_RE = new RegExp('\\b(?:' + CUES.map(function (c) { return c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }).join('|') + ')\\b');
  var SIMILAR_RE = /\b(?:similar|like this|same as this|alternative|alternatives|more like|something like)\b/;
  var MIN_WORDS_FOR_SENTENCE = 6;

  function normalForIntent(q) { return tokenize(asciiFold(cleanText(q)).toLowerCase().replace(/'/g, '')).join(' '); }
  function wantsSimilar(q) { return SIMILAR_RE.test(normalForIntent(q)); }
  function isConversational(q) {
    var text = normalForIntent(q);
    if (!text) return false;
    if (parseBudget(String(q)).present) return true;
    if (CUE_RE.test(text)) return true;
    return text.split(' ').length >= MIN_WORDS_FOR_SENTENCE;
  }

  X.POUND = POUND; X.MAX_QUERY_CHARS = MAX_QUERY_CHARS; X.cleanText = cleanText; X.asciiFold = asciiFold; X.tokenize = tokenize;
  X.stem = stem; X.stemTokens = stemTokens; X.osaDistance = osaDistance; X.parseBudget = parseBudget;
  X.isConversational = isConversational; X.wantsSimilar = wantsSimilar;
})(window);